/**
 * Pure parsing + shaping helpers for the `get_node_neighborhood` tool result.
 * No React, no IO — the controller (GetNodeNeighborhoodToolUI) and the
 * presentational view both consume these.
 */

export interface GetNodeNeighborhoodArgs {
  node_id: string
  hops?: number
}

export interface NeighborRef {
  element_id: string
  name: string
  labels: string[]
  /** Server-built 1-line subtitle from `_entity_summary` / property fallback. */
  subtitle?: string
}

export interface RelTypeCount {
  type: string
  count: number
}

export interface GetNodeNeighborhoodResult {
  status: 'ok' | 'empty' | 'error'
  hops?: number
  center?: NeighborRef
  neighbor_count?: number
  neighbors_by_label?: Record<string, NeighborRef[]>
  relationship_types?: RelTypeCount[]
  /** IDs only — the UI calls `/graph/hydrate` to materialize a full
   * `GraphView` when the user clicks "Show on graph" / "Add" / "Save". Keeps
   * the agent context payload small (inlining the whole neighbourhood with
   * properties is what blew the budget). */
  hydrate?: { node_ids: string[]; relationship_ids: string[]; label?: string }
  error?: string
}

export function parseResult(
  raw: GetNodeNeighborhoodResult | string | undefined
): GetNodeNeighborhoodResult | null {
  if (raw == null) return null
  if (typeof raw === 'string') {
    try {
      return JSON.parse(raw) as GetNodeNeighborhoodResult
    } catch {
      return { status: 'error', error: raw }
    }
  }
  return raw
}

export function visibleLabels(labels: string[]): string[] {
  return labels.filter(l => !l.startsWith('__'))
}

export function primaryLabel(labels: string[]): string | undefined {
  return visibleLabels(labels)[0]
}

/** Groups sorted by descending size — the largest label group renders first
 *  and is auto-selected when the result lands. */
export function sortedGroupEntries(
  parsed: GetNodeNeighborhoodResult | null
): [string, NeighborRef[]][] {
  const groups = parsed?.neighbors_by_label ?? {}
  return Object.entries(groups).sort((a, b) => b[1].length - a[1].length)
}

/** Hydrate node ids drive the action footer's entity-aware hint + actions. */
export function extractEntityIds(parsed: GetNodeNeighborhoodResult | null): string[] {
  return parsed?.hydrate?.node_ids ?? []
}
